import ElectricBoltIcon from '@mui/icons-material/ElectricBolt'
import { Box, Stack, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import { useBranding } from '@/contexts/BrandingContext'
import { brandLook } from '@/theme/brands'
import BrandMark from './BrandMark'
import MaskedLogo from './MaskedLogo'

/**
 * The org's own logo, then the resolved brand's logo, with a
 * customer-neutral platform fallback last.
 *
 * `orgLogo` is supplied only by the authenticated app shell (design D7,
 * org-sidebar-logo) — pre-auth callers (login, marketing) pass nothing and
 * keep the brand-only behaviour below unchanged, since no org is known
 * there. When present, the org's own logo takes precedence over the
 * resolved brand's `logo_url` (partner-branding-ui: "org logo outranks
 * brand logo in the shell"), rendered as a `MaskedLogo` — a CSS mask tinted
 * with the theme's accent colour — rather than an `<img>`, so the org's SVG
 * is never fetched or inlined here.
 *
 * Renders the brand's `logo_url` when set (partner hosts, or any brand row
 * with a logo configured). Otherwise, on a platform brand's own domain, renders
 * that brand's mark and split wordmark (theme/brands.js); anywhere else, a
 * neutral mark — bolt + product name — so no customer's branding ever appears
 * as the code-level default.
 * `compact` renders the org's square mark/logo, then the brand logo, then
 * the bolt alone (collapsed sidebar).
 */
const BrandLogo = ({ height = 60, compact = false, orgLogo, sx }) => {
    const { brand } = useBranding()
    const orgLabel = orgLogo?.name || brand.product_name || 'Logo'

    if (compact) {
        // Compact precedence (design D7): square mark, then the full logo
        // scaled to fit, then the brand fallback below.
        const compactOrgSrc = orgLogo?.markUrl || orgLogo?.logoUrl
        if (compactOrgSrc) {
            return (
                <MaskedLogo
                    src={compactOrgSrc}
                    label={orgLabel}
                    width={32}
                    height={32}
                    sx={sx}
                />
            )
        }
    } else if (orgLogo?.logoUrl) {
        return (
            <MaskedLogo
                src={orgLogo.logoUrl}
                label={orgLabel}
                width="100%"
                height={height}
                sx={{ maxWidth: 200, ...sx }}
            />
        )
    }

    if (brand.logo_url) {
        return (
            <Box
                component="img"
                src={brand.logo_url}
                alt={brand.product_name || 'Logo'}
                sx={{
                    height: compact ? Math.min(height, 32) : height,
                    ...(compact && { width: 32 }),
                    maxWidth: '100%',
                    objectFit: 'contain',
                    ...sx
                }}
            />
        )
    }

    // A platform brand host (joulelingo.com, kilosight.com) shows that
    // brand's mark and split wordmark. A partner host never does: there the
    // neutral bolt stands in until the partner sets a logo, so our product's
    // logo never appears under someone else's name.
    const brandKey = !brand.is_partner && brand.brand_key
    if (brandKey && brandLook(brandKey).hasMark) {
        if (compact) {
            return <BrandMark brandKey={brandKey} size={28} sx={sx} />
        }
        const [lead, tail] = brandLook(brandKey).wordmark || [
            brand.product_name,
            ''
        ]
        return (
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ height, ...sx }}
            >
                <BrandMark
                    brandKey={brandKey}
                    size={Math.min(height * 0.6, 40)}
                />
                <Typography
                    component="span"
                    sx={(theme) => ({
                        fontFamily: theme.typography.h1.fontFamily,
                        fontSize: Math.min(height * 0.45, 28),
                        fontWeight: 800,
                        letterSpacing: '-0.03em',
                        color: 'text.primary',
                        lineHeight: 1
                    })}
                >
                    {lead}
                    <Box
                        component="span"
                        sx={{ color: 'primary.main', fontWeight: 600 }}
                    >
                        {tail}
                    </Box>
                </Typography>
            </Stack>
        )
    }

    if (compact) {
        return <ElectricBoltIcon color="primary" sx={sx} />
    }

    return (
        <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ height, ...sx }}
        >
            <ElectricBoltIcon
                color="primary"
                sx={{ fontSize: Math.min(height * 0.6, 40) }}
            />
            <Typography
                color="text.primary"
                sx={{
                    fontSize: Math.min(height * 0.45, 28),
                    fontWeight: 600,
                    letterSpacing: '-0.02em'
                }}
            >
                {brand.product_name || 'Power'}
            </Typography>
        </Stack>
    )
}

BrandLogo.propTypes = {
    height: PropTypes.number,
    compact: PropTypes.bool,
    orgLogo: PropTypes.shape({
        logoUrl: PropTypes.string,
        markUrl: PropTypes.string,
        name: PropTypes.string
    }),
    sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array])
}

export default BrandLogo
